import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { type ColumnDef, type Row } from "@tanstack/react-table";
import { Select, SelectItem, Spinner, Button } from "@heroui/react";
import { useS2DashboardContext } from "../context/S2DashboardProvider";
import FundPickerBar from "../components/FundPickerBar";
import { api, getPortfolioPositionsDownloadUrl } from "../services/api";
import { DownloadButton } from "@/components/DownloadButton";
import { DataTable } from "@/components/data-table";
import PDFModal from "../components/PDFModal";
import { peCol } from "../model/tableHelpers";
import type { PeRow, GroundingState } from "../model/tableHelpers";
import { S2_GROUP_HEADER_CLASS } from "../model/constants";
import { periodKey, findDocForKey } from "../model/utils";

// ─── Column groups (TanStack native nested ColumnDef) ─────────────────────────

function buildPortfolioColumns(
  onSource: (g: GroundingState) => void,
): ColumnDef<PeRow>[] {
  const p = (key: string, label: string, size?: number) =>
    peCol(key, label, { size, onSource });

  return [
    {
      id: "general-information",
      header: "General Information",
      columns: [
        p(
          "portfolio_review_general_information_company_name_long_name",
          "Company Name",
          180,
        ),
        p(
          "portfolio_review_general_information_company_name_short_name",
          "Short Name",
        ),
        p("_entry_date", "Entry Date"),
        p("portfolio_review_general_information_company_website", "Website"),
        p("portfolio_review_general_information_legal_form", "Legal Form"),
        p(
          "portfolio_review_general_information_geography_legal_country",
          "Country",
        ),
        p(
          "portfolio_review_general_information_city_of_the_legal_country",
          "City",
        ),
        p("portfolio_review_general_information_nace_code", "NACE Code"),
        p(
          "portfolio_review_general_information_commercial_register_number",
          "Reg. Number",
        ),
        p("portfolio_review_general_information_lei_code", "LEI Code"),
        p(
          "portfolio_review_general_information_seedpool_fund_investments",
          "Seedpool",
        ),
        p(
          "portfolio_review_general_information_short_description_of_the_business",
          "Description",
        ),
        p("portfolio_review_general_information_comment", "Comment"),
      ],
    },
    {
      id: "investment-details",
      header: "Investment Details",
      columns: [
        p(
          "portfolio_review_general_information_stage_at_initial_investment",
          "Entry Stage",
        ),
        p(
          "portfolio_review_general_information_last_investment_stage",
          "Last Stage",
        ),
        p(
          "portfolio_review_portfolio_positions_first_investment_date",
          "First Inv. Date",
        ),
        p("portfolio_review_portfolio_positions_type_of_investment", "Type"),
        p(
          "portfolio_review_portfolio_positions_public_equity",
          "Public Equity",
        ),
        p(
          "portfolio_review_portfolio_positions_ownership_non_diluted_pct",
          "Ownership % (ND)",
        ),
        p(
          "portfolio_review_portfolio_positions_ownership_fully_diluted_pct",
          "Ownership % (FD)",
        ),
        p(
          "portfolio_review_portfolio_positions_voting_rights_pct",
          "Voting Rights %",
        ),
      ],
    },
    {
      id: "financials-valuation",
      header: "Financials & Valuation",
      columns: [
        p(
          "portfolio_review_portfolio_positions_company_investment_currency",
          "Currency",
        ),
        p("portfolio_review_portfolio_positions_turnover", "Turnover"),
        p("portfolio_review_portfolio_positions_ebitda", "EBITDA"),
        p(
          "portfolio_review_portfolio_positions_total_investment_cost",
          "Total Cost",
        ),
        p(
          "portfolio_review_portfolio_positions_current_costs",
          "Current Costs",
        ),
        p("portfolio_review_portfolio_positions_fair_value", "Fair Value"),
        p("portfolio_review_portfolio_positions_proceeds", "Proceeds"),
        p("portfolio_review_portfolio_positions_dividends_income", "Dividends"),
        p("portfolio_review_portfolio_positions_irr_pct", "Gross IRR"),
        p("portfolio_review_portfolio_positions_moic_mult", "Gross MOIC"),
      ],
    },
    {
      id: "exit-status",
      header: "Exit & Status",
      columns: [
        p(
          "portfolio_review_portfolio_positions_company_investment_state",
          "Status",
        ),
        p(
          "portfolio_review_portfolio_positions_realized_costs_incl_partial_realisation",
          "Realized Costs",
        ),
        p(
          "portfolio_review_portfolio_positions_date_of_partial_exit",
          "Partial Exit Date",
        ),
        p("portfolio_review_portfolio_positions_exit_date", "Exit Date"),
        p("portfolio_review_portfolio_positions_exit_method", "Exit Method"),
      ],
    },
  ];
}

function buildContextGroup(
  onSource: (g: GroundingState) => void,
): ColumnDef<PeRow> {
  const p = (key: string, label: string, size?: number) =>
    peCol(key, label, { size, onSource });

  return {
    id: "context",
    header: "Context",
    columns: [
      p("_fund_label", "Fund", 140),
      p("_reporting_period", "Period", 90),
    ],
  };
}

type FilterMode = "all" | "new" | "exits";
const MOST_RECENT_KEY = "most_recent";

function getMostRecentDocId(
  docIds: string[],
  docMeta: Record<
    string,
    { reporting_date?: string; reporting_period: string }
  >,
): string | null {
  // Normalise every doc to a comparable YYYYMMDD integer (or YYYYQ0 fallback)
  // so all docs are on the same scale regardless of whether reporting_date is set.
  function docScore(docId: string): number {
    const meta = docMeta[docId];
    if (!meta) return 0;
    const d = meta.reporting_date ? Date.parse(meta.reporting_date) : NaN;
    if (!isNaN(d)) {
      const dt = new Date(d);
      return (
        dt.getFullYear() * 10000 + (dt.getMonth() + 1) * 100 + dt.getDate()
      );
    }
    const k = periodKey(meta.reporting_period);
    if (k === "1900_0" || k === "0_0") return 0;
    const [year, quarter] = k.split("_").map(Number);
    // Map Q1→03-31, Q2→06-30, Q3→09-30, Q4→12-31 as YYYYMMDD
    return year * 10000 + quarter * 3 * 100;
  }

  let bestDocId: string | null = null;
  let bestScore = -Infinity;
  for (const docId of docIds) {
    const score = docScore(docId);
    if (score > bestScore) {
      bestScore = score;
      bestDocId = docId;
    }
  }
  return bestDocId;
}

// ─── Row colour callback (business logic here, not in DataTable) ──────────────

function portfolioRowClass(row: Row<PeRow>): string | undefined {
  if (row.original._is_new === "true")
    return "bg-green-50 group-hover:bg-green-100";
  if (row.original._is_exit === "true")
    return "bg-red-50 group-hover:bg-red-100 text-gray-400";
  return undefined;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PortfolioView() {
  const {
    funds,
    selectedFundIds,
    selectedFundId,
    docMeta,
    portSelectedDocId,
    setPortSelectedDocId,
  } = useS2DashboardContext();

  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [grounding, setGrounding] = useState<GroundingState | null>(null);

  const activeFundIds = useMemo(() => {
    if (selectedFundIds.length > 0) return Array.from(new Set(selectedFundIds));
    return selectedFundId ? [selectedFundId] : [];
  }, [selectedFundIds, selectedFundId]);

  const isMulti = activeFundIds.length > 1;

  const getPortfolioDocIds = useCallback(
    (fundId: string) => {
      const fund = funds.find((f) => f.fund_id === fundId);
      if (!fund) return [];
      return fund.doc_ids.filter(
        (docId) => docMeta[docId]?.has_portfolio_review,
      );
    },
    [funds, docMeta],
  );

  const columns = useMemo(() => {
    const groups = buildPortfolioColumns(setGrounding);
    return isMulti ? [buildContextGroup(setGrounding), ...groups] : groups;
  }, [isMulti, setGrounding]);

  const pinnedLeft = isMulti
    ? [
        "_fund_label",
        "_reporting_period",
        "portfolio_review_general_information_company_name_long_name",
      ]
    : ["portfolio_review_general_information_company_name_long_name"];

  const getRowClassName = useCallback(
    (row: Row<PeRow>) => portfolioRowClass(row),
    [],
  );

  const sourceOptions = useMemo(() => {
    const opts: { key: string; label: string }[] = [];
    if (activeFundIds.length === 0) return opts;

    if (isMulti) {
      opts.push({ key: MOST_RECENT_KEY, label: "Most Recent" });
    }

    const fundMaps = activeFundIds.map((fundId) => {
      const map = new Map<string, string>();
      getPortfolioDocIds(fundId).forEach((docId) => {
        const p = docMeta[docId]?.reporting_period;
        if (p) {
          const k = periodKey(p);
          if (k !== "1900_0" && k !== "0_0" && !map.has(k)) map.set(k, docId);
        }
      });
      return map;
    });

    const [first, ...rest] = fundMaps;
    const commonKeys = [...(first?.keys() ?? [])].filter((k) =>
      rest.every((m) => m.has(k)),
    );

    commonKeys.sort((a, b) => {
      const [ya, qa] = a.split("_").map(Number);
      const [yb, qb] = b.split("_").map(Number);
      return yb !== ya ? yb - ya : qb - qa;
    });

    commonKeys.forEach((k) => {
      const labels = [
        ...new Set(
          fundMaps.map((m) => {
            const docId = m.get(k);
            return docId ? (docMeta[docId]?.reporting_period ?? k) : k;
          }),
        ),
      ];
      opts.push({ key: k, label: labels.join(" / ") });
    });

    return opts;
  }, [activeFundIds, getPortfolioDocIds, docMeta, isMulti]);

  const defaultKey = sourceOptions[0]?.key ?? "";
  const effectivePeriodKey = portSelectedDocId ?? defaultKey;

  const validKey = sourceOptions.some((o) => o.key === effectivePeriodKey)
    ? effectivePeriodKey
    : defaultKey;
  const isMostRecent = validKey === MOST_RECENT_KEY;

  useEffect(() => {
    if (!validKey) {
      if (portSelectedDocId !== null) setPortSelectedDocId(null);
      return;
    }
    if (portSelectedDocId !== validKey) {
      setPortSelectedDocId(validKey);
    }
  }, [portSelectedDocId, setPortSelectedDocId, validKey]);

  const queryConfigs = useMemo(() => {
    return activeFundIds.flatMap((fundId) => {
      const portfolioDocIds = getPortfolioDocIds(fundId);
      if (portfolioDocIds.length === 0) return [];
      const docId = isMostRecent
        ? getMostRecentDocId(portfolioDocIds, docMeta)
        : findDocForKey(portfolioDocIds, validKey, docMeta);
      if (!docId) return [];
      return [{ fundId, docId }];
    });
  }, [activeFundIds, getPortfolioDocIds, isMostRecent, validKey, docMeta]);

  const queryResults = useQueries({
    queries: queryConfigs.map(({ fundId, docId }) => ({
      queryKey: ["portfolio", fundId, docId],
      queryFn: () => api.getPortfolio(fundId, docId),
      staleTime: 30_000,
    })),
  });

  const invQueries = useQueries({
    queries: activeFundIds.map((fundId) => ({
      queryKey: ["investments", fundId],
      queryFn: () => api.getInvestments(fundId),
      staleTime: 60_000,
    })),
  });

  const invMap = useMemo(() => {
    const map: Record<
      string,
      Record<
        string,
        { entryDate: string; exitDate: string; firstSeenPeriod: string }
      >
    > = {};
    activeFundIds.forEach((fundId, i) => {
      const perFund: Record<
        string,
        { entryDate: string; exitDate: string; firstSeenPeriod: string }
      > = {};
      const invRows = invQueries[i]?.data ?? [];
      invRows.forEach((row) => {
        const id = row["investments_summary_inv_id"] || "";
        if (!id || id === "nan") return;
        perFund[id] = {
          entryDate: row["investments_summary_actual_entry_date"] || "",
          exitDate: row["investments_summary_actual_exit_date"] || "",
          firstSeenPeriod: row["investments_summary_first_seen_period"] || "",
        };
      });
      map[fundId] = perFund;
    });
    return map;
  }, [activeFundIds, invQueries]);

  const isLoading = queryResults.some((q) => q.isLoading);

  const rows = useMemo<PeRow[]>(() => {
    return queryConfigs.flatMap(({ fundId, docId }, i) => {
      const fund = funds.find((f) => f.fund_id === fundId);
      const fundName = fund?.fund_name || fundId;
      const fundData = queryResults[i]?.data ?? [];
      const fundPeriod = docMeta[docId]?.reporting_period || "";
      const selectedPeriodKey = periodKey(fundPeriod);
      const fundInvMap = invMap[fundId] ?? {};

      return fundData.map((row, rowIndex) => {
        const invId = row["portfolio_review_system_identifiers_inv_id"] || "";
        const inv = fundInvMap[invId];
        const entryDate = inv?.entryDate || "";
        const exitDate = inv?.exitDate || "";

        let isNew = false;
        if (inv?.firstSeenPeriod) {
          isNew = periodKey(inv.firstSeenPeriod) === selectedPeriodKey;
        }

        let isExit = false;
        if (exitDate && exitDate !== "nan") {
          const exitD = new Date(exitDate);
          if (!isNaN(exitD.getTime())) {
            const [selYear, selQ] = selectedPeriodKey.split("_").map(Number);
            const exitYear = exitD.getFullYear();
            const exitQ = Math.ceil((exitD.getMonth() + 1) / 3);
            isExit =
              exitYear < selYear || (exitYear === selYear && exitQ <= selQ);
          }
        }

        const rowKey = `${fundId}::${docId}::${invId || row["portfolio_review_general_information_company_name_long_name"] || rowIndex}`;

        return {
          ...row,
          id: rowKey,
          _fund_label: fundName,
          _reporting_period: fundPeriod,
          _doc_id: docId,
          _row_index: String(rowIndex),
          _file_name: docMeta[docId]?.file_name || "",
          _entry_date: entryDate,
          _is_new: isNew ? "true" : "false",
          _is_exit: isExit ? "true" : "false",
        };
      });
    });
  }, [queryConfigs, queryResults, funds, docMeta, invMap]);

  // Deduplicate only exact frontend row identities so that two legitimate
  // fund contexts sharing the same underlying doc/row index are not collapsed.
  const dedupedRows = useMemo(() => {
    const seen = new Set<string>();
    return rows.filter((row) => {
      const key = String(row.id ?? "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (filterMode === "new")
      return dedupedRows.filter((r) => r._is_new === "true");
    if (filterMode === "exits")
      return dedupedRows.filter((r) => r._is_exit === "true");
    return dedupedRows;
  }, [dedupedRows, filterMode]);

  return (
    <div className="flex flex-col gap-4">
      <FundPickerBar mode="single" />

      {activeFundIds.length === 0 ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select one or more funds above
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            {sourceOptions.length > 1 && (
              <div className="w-72">
                <Select
                  label="Source"
                  size="sm"
                  selectedKeys={new Set([validKey])}
                  onSelectionChange={(keys) => {
                    const val = Array.from(keys)[0] as string;
                    setPortSelectedDocId(val);
                  }}
                  aria-label="Select portfolio source"
                >
                  {sourceOptions.map((o) => (
                    <SelectItem key={o.key}>{o.label}</SelectItem>
                  ))}
                </Select>
              </div>
            )}
            {isLoading && <Spinner size="sm" />}
            <span className="text-xs text-gray-400">
              {visibleRows.length}/{dedupedRows.length} investments ·{" "}
              {activeFundIds.length} fund
              {activeFundIds.length !== 1 ? "s" : ""}
            </span>
            <div className="ml-auto flex gap-1">
              <Button
                size="sm"
                variant={filterMode === "all" ? "solid" : "flat"}
                color={filterMode === "all" ? "primary" : "default"}
                onPress={() => setFilterMode("all")}
                className="text-xs h-7 px-3"
              >
                All
              </Button>
              <Button
                size="sm"
                variant={filterMode === "new" ? "solid" : "flat"}
                color={filterMode === "new" ? "success" : "default"}
                onPress={() => setFilterMode("new")}
                className="text-xs h-7 px-3"
              >
                New only
              </Button>
              <Button
                size="sm"
                variant={filterMode === "exits" ? "solid" : "flat"}
                color={filterMode === "exits" ? "danger" : "default"}
                onPress={() => setFilterMode("exits")}
                className="text-xs h-7 px-3"
              >
                Exits only
              </Button>
            </div>
          </div>
          {!isLoading && (
            <div className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
              <DataTable<PeRow, unknown>
                table_name="pe-portfolio"
                columns={columns}
                data={visibleRows}
                pinnedLeft={pinnedLeft}
                getRowClassName={getRowClassName}
                enableHorizontalScroll
                groupHeaderClassName={S2_GROUP_HEADER_CLASS}
                pageSize={1000}
              />
              <div className="flex items-center border-t border-slate-200 px-4 py-3">
                <DownloadButton
                  href={getPortfolioPositionsDownloadUrl()}
                  filename={`s2_portfolio_positions_${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`}
                  size="sm"
                  text="Download"
                  className="text-sm"
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* PDF grounding modal — managed by this view */}
      <PDFModal
        isOpen={!!grounding}
        onClose={() => setGrounding(null)}
        docId={grounding?.docId ?? ""}
        fileName={grounding?.fileName}
        page={grounding?.page ?? 1}
        quote={grounding?.quote ?? ""}
        rowLabel={grounding?.rowLabel}
        colLabel={grounding?.colLabel}
        value={grounding?.value}
      />
    </div>
  );
}
